import { Link2Off } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/atoms/alert-dialog';

/**
 * Wraps the QuickBooks "Disconnect" affordance with a confirmation
 * dialog that spells out exactly what disconnecting does — and just as
 * importantly, what it does NOT do. Without this, a single misclick on
 * the small "Disconnect" button immediately tears down the connection.
 *
 * The copy mirrors the runtime behavior we ship in qb-oauth.service.ts:
 *
 *   - Disconnect (this dialog) → stops queued sync work, marks the
 *     connection idle. Account mappings + per-entity QB IDs are kept.
 *   - Reconnect to the SAME QuickBooks company → resumes with no loss.
 *   - Reconnect to a DIFFERENT QuickBooks company → the OAuth callback
 *     archives the current mappings under the old realm and either
 *     restores a previously-seen realm's mappings or starts clean.
 *
 * Callers pass the actual mutation as `onConfirm`; this component only
 * owns the confirmation UI.
 */
interface QbDisconnectConfirmDialogProps {
  trigger: ReactNode;
  onConfirm: () => void;
  /** When true, the action button shows a pending label and disables itself. */
  isPending?: boolean;
}

export function QbDisconnectConfirmDialog({
  trigger,
  onConfirm,
  isPending,
}: QbDisconnectConfirmDialogProps) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Link2Off className="h-4 w-4" />
            Disconnect QuickBooks?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>This will immediately:</p>
              <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                <li>Stop all queued and in-flight sync jobs for this tenant.</li>
                <li>Pause inbound updates from QuickBooks until you reconnect.</li>
                <li>Mark the connection as inactive; nothing in QuickBooks is deleted.</li>
              </ul>
              <p>Your data is preserved:</p>
              <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                <li>
                  GL account mapping, payment-method mapping, and sync direction toggles are kept.
                </li>
                <li>
                  Per-entity QuickBooks IDs (customers, invoices, estimates, payments) stay linked.
                </li>
              </ul>
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                If you reconnect to a <strong>different</strong> QuickBooks company, the current
                account&apos;s mappings will be archived so they can be restored if you switch back.
                Reconnecting to <strong>the same</strong> company resumes with no loss.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction variant="destructive" disabled={isPending} onClick={onConfirm}>
            {isPending ? 'Disconnecting…' : 'Disconnect'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
